/**
 * ComparatorCard — generic family-member display card (Phase F.1a / GEN-2997).
 *
 * Reads a single `Comparator` from the canonical order shape (Phase B.2)
 * and renders the same visual surface that the legacy per-slot biological
 * parent cards provided for `mother` / `father` slots. As of F.1c this is
 * the sole family-member render path in `OrderInProgress` and `Review`.
 *
 * F.1c-iii Option B: shared card chrome (outer shell, header + Edit pencil,
 * badge row, field grid, status pill, empty state) is delegated to
 * `<FamilyMemberDisplayCard>`. The comparator-specific bits (`TwinBadge`,
 * mapping `Comparator` → `FieldSpec[]`) stay local.
 */

import type { Comparator } from '@/ordering/canonicalOrder'
import {
  formatComparatorRelationshipLabel,
  isMultipleBirthRelationship,
} from '@/ordering/comparatorRelationshipOptions'
import {
  FamilyMemberDisplayCard,
  PrimaryGuardianBadge,
  type FieldSpec,
} from './FamilyMemberDisplayCard'
import { formatDobForDisplay } from './familyMemberDisplayHelpers'
import { CalendarMonth, Envelope, Phone, UserCircle } from 'flowbite-react-icons/outline'

const ZYGOSITY_LABELS: Record<string, string> = {
  MONOZYGOTIC: 'Monozygotic',
  DIZYGOTIC: 'Dizygotic',
  UNKNOWN: 'Unknown zygosity',
  NOT_APPLICABLE: '',
}

function TwinBadge({ zygosity }: { zygosity?: string | null }) {
  const label = zygosity && zygosity in ZYGOSITY_LABELS ? ZYGOSITY_LABELS[zygosity] : ''
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border-base bg-background-gray px-2.5 py-1 text-xs font-medium text-text-body dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
      {label ? `Twin · ${label}` : 'Twin'}
    </span>
  )
}

export interface ComparatorCardProps {
  comparator: Comparator
  onEdit?: () => void
  /** Match BiologicalMotherCard / FatherCard attention styling when validation pending. */
  needsAttention?: boolean
  /** Bottom status pill text. Defaults based on `isTestingComparator`. */
  statusMessage?: string
  statusVariant?: 'success' | 'info'
}

export function ComparatorCard({
  comparator,
  onEdit,
  needsAttention = false,
  statusMessage,
  statusVariant,
}: ComparatorCardProps) {
  const demo = comparator.demographic ?? null
  const relationshipLabel =
    formatComparatorRelationshipLabel(comparator.relationship) || 'Family member'
  const name = [demo?.firstName, demo?.lastName]
    .filter((p) => p && String(p).trim())
    .join(' ')
    .trim()
  const emailTrim = demo?.email?.trim() ?? ''
  const phoneTrim = demo?.phone?.trim() ?? ''
  const dobTrim = demo?.dateOfBirth?.trim() ?? ''
  const mrnTrim = demo?.mrn?.trim() ?? ''
  const isPrimaryGuardian = comparator.isPrimaryGuardian === true
  const isMultiple =
    isMultipleBirthRelationship(comparator.relationship) || comparator.multipleBirth != null
  const zygosity = comparator.multipleBirth?.zygosity ?? null

  const isTesting = comparator.isTestingComparator === true
  const bottomStatusText =
    statusMessage ?? (isTesting ? 'Sent to lab' : 'Not included')
  const bottomStatusVariant = statusVariant ?? (isTesting ? 'success' : 'info')

  const fields: FieldSpec[] = [
    { icon: Envelope, value: emailTrim, title: emailTrim || undefined },
    { icon: Phone, value: phoneTrim, title: phoneTrim || undefined },
    {
      icon: CalendarMonth,
      inlineLabel: 'DOB ',
      value: dobTrim ? formatDobForDisplay(dobTrim) : '',
    },
    { icon: UserCircle, inlineLabel: 'MRN ', value: mrnTrim },
  ]

  const badges =
    isPrimaryGuardian || isMultiple ? (
      <>
        {isPrimaryGuardian && <PrimaryGuardianBadge>Primary guardian</PrimaryGuardianBadge>}
        {isMultiple && <TwinBadge zygosity={zygosity} />}
      </>
    ) : null

  return (
    <FamilyMemberDisplayCard
      title={relationshipLabel}
      onEdit={onEdit}
      editAriaLabel={`Edit ${relationshipLabel.toLowerCase()}`}
      needsAttention={needsAttention}
      badges={badges}
      nameLine={name || null}
      fields={fields}
      status={{ text: bottomStatusText, variant: bottomStatusVariant, successLead: 'check' }}
    />
  )
}
